import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Loader2, Download, Radar } from "lucide-react";

/**
 * Deployment coverage: every device the phased deployment should reach,
 * including the ones Intune has never reported, with each device's last
 * Intune check-in. Exports to CSV for service desk mop-up.
 *
 * Loads with the drill-down. The backend reads the phase groups from Entra
 * and looks up only the devices that need chasing in Intune, so it stays
 * quick. Feed: GET /api/deployments/{id}/coverage.
 */

// Display order and colours. Keys match services/deployment_coverage.py.
const STATUS_STYLE = {
  failed:             { cls: "bg-rose-500/20 text-rose-300 border-rose-500/30" },
  not_reported:       { cls: "bg-orange-500/20 text-orange-300 border-orange-500/30" },
  pending:            { cls: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  deferred:           { cls: "bg-sky-500/20 text-sky-300 border-sky-500/30" },
  skipped:            { cls: "bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30" },
  not_targeted:       { cls: "bg-violet-500/20 text-violet-300 border-violet-500/30" },
  no_longer_targeted: { cls: "bg-white/10 text-white/70 border-white/15" },
  excluded:           { cls: "bg-white/10 text-white/60 border-white/15" },
  installed:          { cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
};

const STATUS_HELP = {
  not_reported: "In the current phase group, but Intune has no install state for this app on the device. Check the last check-in.",
  not_targeted: "Has an older version in inventory but is in no phase group — it arrived after the phase was built.",
  no_longer_targeted: "In an earlier phase group only. Each phase replaces the app's assignment, so this device is no longer assigned.",
  skipped: "ALICE could not add the device to the phase group (usually no matching Entra device).",
  excluded: "In an exclusion group: the family's governance leaves its version alone.",
};

// Exports where every column can carry text a device or user name brought
// with it, so neutralise spreadsheet formulas as well as quoting.
const csvCell = (value) => {
  let s = value === null || value === undefined ? "" : String(value);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const CSV_COLUMNS = [
  ["Device", (r) => r.device_name],
  ["Status", (r) => r.status_label],
  ["Phase group(s)", (r) => (r.phases || []).join(" ")],
  ["In current phase group", (r) => (r.in_current_group ? "Yes" : "No")],
  ["User", (r) => r.user],
  ["Intune last check-in", (r) => r.intune_last_sync],
  ["Days since check-in", (r) => r.days_since_sync],
  ["Version(s) in inventory", (r) => (r.inventory_versions || []).join(" ")],
  ["Reported app version", (r) => r.reported_app_version],
  ["Install state", (r) => r.install_state],
  ["Error code", (r) => r.error_code],
  ["State detail", (r) => r.install_state_detail],
  ["State detail meaning", (r) => r.install_state_detail_meaning],
  ["Install state changed", (r) => r.state_changed_at],
  ["Skip reason", (r) => r.skip_reason],
  ["OS version", (r) => r.os_version],
  ["Compliance", (r) => r.compliance_state],
  ["In Intune", (r) => (r.in_intune ? "Yes" : "No")],
  ["Entra device ID", (r) => r.entra_device_id],
  ["Intune device ID", (r) => r.intune_device_id],
];

const downloadCsv = (rows, filename) => {
  const lines = [CSV_COLUMNS.map(([h]) => csvCell(h)).join(",")];
  rows.forEach((r) => lines.push(CSV_COLUMNS.map(([, get]) => csvCell(get(r))).join(",")));
  // BOM so Excel opens UTF-8 device and user names correctly.
  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const safeFilePart = (s) => String(s || "").replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "");

// "Needs action" is everything the service desk has to chase.
const isGap = (r) => !["installed", "excluded", "no_longer_targeted"].includes(r.status);

const formatDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

const StatusPill = ({ status, label }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border whitespace-nowrap ${(STATUS_STYLE[status] || STATUS_STYLE.excluded).cls}`}
    title={STATUS_HELP[status] || ""}
  >
    {label}
  </span>
);

const DeploymentCoverage = ({ packageId, getHeaders }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("gaps");

  const load = async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/deployments/${packageId}/coverage`, { headers: await getHeaders(), signal });
      setData(res.data);
    } catch (e) {
      if (axios.isCancel?.(e) || e?.name === "CanceledError") return;
      setError(e?.response?.data?.detail || e?.message || "Could not load coverage");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!packageId) return undefined;
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [packageId]); // load intentionally omitted (recreated each render)

  const rows = data?.rows || [];
  const counts = data?.summary?.counts || {};
  const labels = data?.status_labels || {};
  const shown = useMemo(
    () => (filter === "all" ? rows : filter === "gaps" ? rows.filter(isGap) : rows.filter((r) => r.status === filter)),
    [rows, filter],
  );

  const exportShown = () => {
    if (!shown.length) {
      toast.error("No devices to export");
      return;
    }
    const date = new Date().toISOString().split("T")[0];
    downloadCsv(shown, `${safeFilePart(data.app_name)}_${safeFilePart(data.version)}_coverage_${filter}_${date}.csv`);
    toast.success(`Exported ${shown.length} device${shown.length === 1 ? "" : "s"}`);
  };

  return (
    <section data-testid="drill-down-coverage">
      <h3 className="text-sm font-semibold text-white/80 flex items-center gap-2 mb-3">
        <Radar className="w-4 h-4" /> Coverage
        {data?.summary && (
          <span className="text-xs text-white/60 font-normal">
            ({data.summary.reported} of {data.summary.targeted} in the current phase group reported
            {data.summary.current_phase ? ` · Phase ${data.summary.current_phase}` : ""})
          </span>
        )}
      </h3>

      {!data && loading && (
        <div className="text-xs text-white/60 flex items-center gap-2" data-testid="coverage-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Checking every targeted device…
        </div>
      )}
      {!data && !loading && error && (
        <div className="text-xs space-y-2">
          <p className="text-rose-400" data-testid="coverage-error">{error}</p>
          <Button
            type="button" size="sm" variant="outline" onClick={() => load()}
            className="h-6 px-2 text-[11px] bg-white/5 border-white/15 text-white hover:bg-white/10"
          >
            Try again
          </Button>
        </div>
      )}

      {data && (
        <div className="space-y-3">
          {!data.phased && (
            <p className="text-xs text-amber-300">
              No phased rollout found for this Intune app, so only devices Intune reported are listed.
            </p>
          )}
          {(data.live_lookup_failed > 0 || data.live_lookup_capped) && (
            <p className="text-xs text-amber-300">
              {data.live_lookup_capped
                ? `Only the first ${data.live_lookups} devices were checked live in Intune; `
                : `${data.live_lookup_failed} device${data.live_lookup_failed === 1 ? "" : "s"} couldn't be checked live in Intune; `}
              their last check-in comes from ALICE's last device sync, where there is one.
            </p>
          )}

          <div className="flex flex-wrap gap-1.5" data-testid="coverage-filters">
            {[["gaps", "Needs action", rows.filter(isGap).length], ["all", "All", rows.length],
              ...Object.keys(STATUS_STYLE).filter((s) => counts[s]).map((s) => [s, labels[s] || s, counts[s]])]
              .map(([key, label, n]) => (
                <button
                  key={key} type="button" onClick={() => setFilter(key)}
                  className={`px-2 py-0.5 rounded-full text-[11px] border ${
                    filter === key ? "bg-white text-[#0F172A] border-white" : "bg-white/5 text-white/80 border-white/15 hover:bg-white/10"
                  }`}
                  title={STATUS_HELP[key] || ""}
                  data-testid={`coverage-filter-${key}`}
                >
                  {label} · {n}
                </button>
              ))}
            <div className="ml-auto flex gap-1.5">
              <Button
                type="button" size="sm" variant="outline" onClick={() => load()} disabled={loading}
                className="h-6 px-2 text-[11px] bg-white/5 border-white/15 text-white hover:bg-white/10"
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Reload"}
              </Button>
              <Button
                type="button" size="sm" onClick={exportShown}
                className="h-6 px-2 text-[11px]"
                data-testid="coverage-export-btn"
              >
                <Download className="w-3 h-3 mr-1" /> Export CSV
              </Button>
            </div>
          </div>
          {error && <p className="text-xs text-rose-400">{error}</p>}

          {shown.length === 0 ? (
            <p className="text-xs text-white/50">No devices in this view.</p>
          ) : (
            <div className="overflow-x-auto border border-white/10 rounded-lg">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase text-white/60 bg-white/5">
                    <th className="px-3 py-2 font-medium">Device</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">User</th>
                    <th className="px-3 py-2 font-medium">Intune check-in</th>
                    <th className="px-3 py-2 font-medium">Version</th>
                    <th className="px-3 py-2 font-medium">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.slice(0, 200).map((r, i) => (
                    <tr key={`${r.entra_device_id || r.device_name}-${i}`} className="border-t border-white/5">
                      <td className="px-3 py-2 text-white/90">
                        {r.device_name || "—"}
                        {r.phases?.length > 0 && (
                          <div className="text-[10px] text-white/40">Phase {r.phases.join(", ")}</div>
                        )}
                      </td>
                      <td className="px-3 py-2"><StatusPill status={r.status} label={r.status_label} /></td>
                      <td className="px-3 py-2 text-white/70 text-xs">{r.user || "—"}</td>
                      <td className="px-3 py-2 text-xs">
                        {r.intune_last_sync ? (
                          <span className={r.days_since_sync >= 14 ? "text-rose-300" : r.days_since_sync >= 7 ? "text-amber-300" : "text-white/70"}>
                            {formatDate(r.intune_last_sync)}
                            <span className="text-white/40"> · {r.days_since_sync}d</span>
                          </span>
                        ) : (
                          <span className="text-white/40">{r.in_intune ? "—" : "Not in Intune"}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-white/70">
                        {r.reported_app_version || (r.inventory_versions || []).join(", ") || "—"}
                      </td>
                      <td className="px-3 py-2 text-xs text-white/60">
                        {r.error_code && <code className="text-[11px] bg-white/5 px-1.5 py-0.5 rounded">{r.error_code}</code>}
                        {r.install_state_detail_meaning && <div className="text-[10px]">{r.install_state_detail_meaning}</div>}
                        {r.skip_reason && <div className="text-[10px]">{r.skip_reason}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {shown.length > 200 && (
                <div className="px-3 py-2 text-xs text-white/50">
                  + {shown.length - 200} more — Export CSV includes every device in this view.
                </div>
              )}
            </div>
          )}
          <p className="text-[10px] text-white/40">Generated {new Date(data.generated_at).toLocaleString()}</p>
        </div>
      )}
    </section>
  );
};

export default DeploymentCoverage;
